import { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Alert,
  Form,
} from 'react-bootstrap';
import { AgreementInfo } from '../models';
import './Common.scss';

interface ReserveData {
  name: string;
  email: string;
  date: Date;
  time: string;
  menu: string;
  menuDetails: any;
  totalPrice: number;
  agreements: AgreementInfo;
}

export default function ReservePreviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const reserveData = location.state as ReserveData;

  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'bank' | 'cash' | ''>('');

  const handlePaymentMethodChange = (method: 'bank' | 'cash') => {
    setPaymentMethod(method);
    setPaymentError(null);
  };

  // 予約データが存在しない場合はReservePageに戻る
  if (!reserveData) {
    navigate('/reserve');
    return null;
  }

  const handleSubmit = async () => {
    setIsProcessing(true);
    setPaymentError(null);

    try {
      if (!paymentMethod) {
        throw new Error('決済方法を選択してください。');
      }

      // 日付をローカルタイムゾーンでYYYY-MM-DD形式に変換（タイムゾーンの影響を避ける）
      const formatDateForAPI = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const reservePayload = {
        ...reserveData,
        date: formatDateForAPI(reserveData.date),
        paymentMethod: paymentMethod,
        amount: reserveData.totalPrice,
      };

      console.log('送信データ:', reservePayload);

      // ファイルがある場合はFormDataを使用
      let bodyData: string | FormData;
      let headers: Record<string, string>;

      const formDataForSubmit = new FormData();
      if (reserveData.menuDetails?.ensoficReiki?.photo) {
        formDataForSubmit.append('data', JSON.stringify(reservePayload));
        formDataForSubmit.append(
          'photo',
          reserveData.menuDetails.ensoficReiki.photo
        );
        bodyData = formDataForSubmit;
        headers = {};
      } else {
        formDataForSubmit.append('data', JSON.stringify(reservePayload));
        bodyData = formDataForSubmit;
        headers = {};
      }

      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const res = await fetch(`${backendUrl}/api/v1/reserve/create`, {
        method: 'POST',
        headers,
        body: bodyData,
      });

      const data = await res.json();

      console.log('レスポンス:', { status: res.status, data });

      if (res.ok) {
        setPaymentSuccess(true);
        setTimeout(() => {
          window.location.href = '/success';
        }, 2000);
      } else {
        throw new Error(data.message || '決済処理に失敗しました');
      }
    } catch (error) {
      setPaymentError(
        error instanceof Error
          ? error.message
          : '予期しないエラーが発生しました'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long',
    }).format(new Date(date));
  };

  const renderMenuDetailsPreview = () => {
    const { menu, menuDetails } = reserveData;

    switch (menu) {
      case 'クリスタルヒーリング':
        return (
          <div>
            <p>
              <strong>セッション回数:</strong>{' '}
              {menuDetails.crystalHealing?.sessions}回
            </p>
            <p>
              <strong>施術部位:</strong>{' '}
              {menuDetails.crystalHealing?.areas?.join(', ')}
            </p>
          </div>
        );

      case 'DNAアクティベーション':
        return (
          <div>
            <p>
              <strong>種類:</strong> {menuDetails.dnaActivation?.type}
            </p>
          </div>
        );

      case 'エンソフィックレイキ':
        return (
          <div>
            <p>
              <strong>セッション:</strong>{' '}
              {menuDetails.ensoficReiki?.sessionType}
            </p>
            <p>
              <strong>ヒーリング種類:</strong>{' '}
              {menuDetails.ensoficReiki?.healingType}
            </p>
            {menuDetails.ensoficReiki?.sessionType?.startsWith('遠隔') && (
              <>
                <p>
                  <strong>お名前:</strong> {menuDetails.ensoficReiki?.name}
                </p>
                <p>
                  <strong>年齢:</strong> {menuDetails.ensoficReiki?.age}歳
                </p>
                <p>
                  <strong>住所:</strong> {menuDetails.ensoficReiki?.address}
                </p>
                {menuDetails.ensoficReiki?.photo && (
                  <p>
                    <strong>顔写真:</strong>{' '}
                    {menuDetails.ensoficReiki.photo.name}
                  </p>
                )}
                {menuDetails.ensoficReiki?.badHabits && (
                  <p>
                    <strong>改善したい悪癖:</strong>{' '}
                    {menuDetails.ensoficReiki.badHabits}
                  </p>
                )}
              </>
            )}
          </div>
        );

      case '神聖幾何学':
        return (
          <div>
            <p>
              <strong>パターン:</strong> {menuDetails.sacredGeometry?.pattern}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container fluid>
      <h2 className="mb-4 text-center text-success white-stroke">
        予約内容確認
      </h2>
      <Row className="justify-content-center">
        <Col lg={8} md={8} sm={10} xs={12} className="mb-4">
          {/* 予約内容の表示 */}
          <Card className="mb-4 border-success">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">予約内容</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>お名前:</strong> {reserveData.name}
                  </p>
                  <p>
                    <strong>メールアドレス:</strong> {reserveData.email}
                  </p>
                  <p>
                    <strong>予約日:</strong> {formatDate(reserveData.date)}
                  </p>
                  <p>
                    <strong>予約時間:</strong> {reserveData.time}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>メニュー:</strong> {reserveData.menu}
                  </p>
                  {renderMenuDetailsPreview()}
                  <p className="text-success">
                    <strong>
                      合計金額: ¥{reserveData.totalPrice.toLocaleString()}
                    </strong>
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* 決済方法選択 */}
          <Card className="mb-4 border-primary">
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">お支払い方法</h5>
            </Card.Header>
            <Card.Body>
              <Form>
                <Form.Group>
                  <Form.Label className="mb-3">
                    <strong>決済方法をお選びください</strong>
                  </Form.Label>

                  <Form.Check
                    type="radio"
                    id="bank-transfer"
                    name="payment-method"
                    label={
                      <div>
                        <strong>銀行振込（事前お支払い）</strong>
                        <div className="text-muted small mt-1">
                          予約確定後に振込先をメールでお送りします。
                          <br />
                          振込確認後、予約が確定となります。
                        </div>
                      </div>
                    }
                    checked={paymentMethod === 'bank'}
                    onChange={() => handlePaymentMethodChange('bank')}
                    className="mb-3"
                  />

                  <Form.Check
                    type="radio"
                    id="cash"
                    name="payment-method"
                    label={
                      <div>
                        <strong>現金（当日お支払い）</strong>
                        <div className="text-muted small mt-1">
                          セッション当日に現金でお支払いください。
                          <br />
                          お釣りのないようご協力をお願いします。
                        </div>
                      </div>
                    }
                    checked={paymentMethod === 'cash'}
                    onChange={() => handlePaymentMethodChange('cash')}
                    className="mb-3"
                  />
                </Form.Group>
              </Form>

              {paymentError && (
                <Alert variant="danger" className="mb-3">
                  {paymentError}
                </Alert>
              )}

              {paymentSuccess && (
                <Alert variant="success" className="mb-3">
                  予約が完了しました。ありがとうございます！
                </Alert>
              )}
            </Card.Body>
          </Card>

          <div className="text-center">
            <Button
              variant="outline-secondary"
              className="me-3"
              onClick={() => {
                // 戻る際に強制リロードで状態をリセット
                window.location.href = '/reserve';
              }}
            >
              戻る
            </Button>

            {paymentMethod ? (
              <Button
                variant="success"
                size="lg"
                onClick={handleSubmit}
                disabled={isProcessing}
                className="px-5"
              >
                {isProcessing ? '処理中...' : '予約を確定する'}
              </Button>
            ) : (
              <Alert variant="info" className="mt-3">
                <i className="bi bi-cash-stack me-2"></i>
                上記から決済方法をお選びください
              </Alert>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}
